import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	ColorPicker,
	IconPicker,
	Input,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	tags?: any[];
	authorOptions?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			category,
			blockPadding,
			blockMargin,
			icon,
			iconGap,
			iconSize,
			iconColor,
			label,
			enableArrows,
			color,
			colorHover,
			textColorHover,
			textColor,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			labelTypography,
			bodyTypography,
		},
		setAttributes,
		categories,
	} = props;

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Query", "magazine-blocks")}>
					<Select
						value={category}
						onChange={(val) => setAttributes({ category: val })}
						label={__("Category", "magazine-blocks")}
						options={[
							{
								value: "all",
								label: __("All", "magazine-blocks"),
							},
							...(categories ?? [])?.map(({ name, id }) => ({
								label: name,
								value: id.toString(),
							})),
						]}
					/>
				</Panel>
				<Panel title={__("Ticker Label", "magazine-blocks")}>
					<Input
						onChange={(val) => setAttributes({ label: val })}
						// @ts-ignore
						labelPosition="top"
						label={__("Label", "magazine-blocks")}
						value={label || ""}
					/>
				</Panel>
				<Panel title={__("Arrows", "magazine-blocks")}>
					<Toggle
						label={__("Enable Arrows", "magazine-blocks")}
						checked={enableArrows || false}
						onChange={() =>
							setAttributes({ enableArrows: !enableArrows })
						}
					/>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<>
						<IconPicker
							value={icon}
							onChange={(val) => setAttributes({ icon: val })}
						/>
						<Slider
							label={__("Size", "magazine-blocks")}
							min={0}
							max={50}
							value={iconSize}
							onChange={(val: any) =>
								setAttributes({ iconSize: val })
							}
							responsive
							units={["px", "em", "%"]}
						/>
						<Slider
							label={__("Gap", "magazine-blocks")}
							min={0}
							max={60}
							value={iconGap}
							onChange={(val: any) =>
								setAttributes({ iconGap: val })
							}
							responsive
							units={["px", "em", "%"]}
						/>
					</>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Ticker Label", "magazine-blocks")}>
					<Typography
						value={labelTypography}
						onChange={(val) =>
							setAttributes({ labelTypography: val })
						}
					/>
					<PopoverDrawer
						label={__("Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{ color: color, id: "color" },
										{
											color: colorHover,
											id: "colorHover",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={color}
										resetKey="color"
										onReset={(v) => {
											setAttributes({ color: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={color}
										onChange={(v) =>
											setAttributes({ color: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={colorHover}
										resetKey="postTitleHoverColor"
										onReset={(v) => {
											setAttributes({
												colorHover: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={colorHover}
										onChange={(v) =>
											setAttributes({ colorHover: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Ticker Body", "magazine-blocks")}>
					<Typography
						value={bodyTypography}
						onChange={(val) =>
							setAttributes({ bodyTypography: val })
						}
					/>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{ color: textColor, id: "textColor" },
										{
											color: textColorHover,
											id: "textColorHover",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={textColor}
										resetKey="textColor"
										onReset={(v) => {
											setAttributes({ textColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={textColor}
										onChange={(v) =>
											setAttributes({ textColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={textColorHover}
										resetKey="postTitleHoverColor"
										onReset={(v) => {
											setAttributes({
												textColorHover: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={textColorHover}
										onChange={(v) =>
											setAttributes({ textColorHover: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: iconColor,
											id: "iconColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>

							<TabPanels>
								<TabPanel>
									<Reset
										saved={iconColor}
										resetKey="iconColor"
										onReset={(v) =>
											setAttributes({ iconColor: v })
										}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									></Reset>
									<ColorPicker
										value={iconColor}
										onChange={(v) =>
											setAttributes({ iconColor: v })
										}
									></ColorPicker>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
